/**
 * Data container for the units table rendered inside an expanded PropertiesListCard. Fetches the
 * listing's units, normalizes them, and hands them to the shared UnitsTableView.
 */
import { usePortfolioFilters } from '../../../contexts/PortfolioFiltersContext';
import { useGetPropertyTableUnits } from '../../../hooks/queries/propertyTable';
import propertyUtils from '../../../utils/propertyUtils';
import { UNIT_NAME_PREFIX } from '../../../utils/unitNameUtils';

import PropertiesConstants from '../../../constants/PropertiesConstants';

import UnitsTableView, { UNIT_TABLE_VARIANT } from './UnitsTableView';

import type { AccountViewListingGroupDto, RentalManagerAddressDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { UnitTableRow } from './UnitsTableView';

type Props = {
  listing: AccountViewListingGroupDto;
  onArchiveClick?: (listingAlias: string, address: RentalManagerAddressDto, unitNumber: string | undefined) => void;
  onEditClick?: (listingAlias: string) => void;
  onTenantAdded?: () => void;
};

const UnitsTable = ({ listing, onArchiveClick, onEditClick, onTenantAdded }: Props) => {
  const groupId = listing.groupId;
  const { searchText, includeArchived } = usePortfolioFilters();

  const {
    dataByGroupId,
    fetchNextPageForGroup,
    hasNextPageByGroupId,
    isFetchingByGroupId,
    isFetchingNextPageByGroupId,
  } = useGetPropertyTableUnits(
    {
      searchText,
      includeArchived,
      includeUnarchived: !includeArchived,
      limit: PropertiesConstants.PROPERTIES_MULTIPLEX_LIMIT_DEFAULT,
    },
    [groupId],
  );

  const unitsData = dataByGroupId[groupId];
  const units = unitsData?.units ?? [];

  const rows: Array<UnitTableRow> = units.map((unit) => {
    const unitTypeLabel = propertyUtils.getUnitTypeLabel({
      propertyType: unit.propertyType,
      listingType: unit.listingType,
    });

    const unitName = unit.unitNumber || unitTypeLabel || '';

    return {
      listingAlias: unit.listingAlias,
      address: listing.address,
      photoKey: unit.photoKey,
      unitLabel: unit.unitNumber ? `${UNIT_NAME_PREFIX} ${unit.unitNumber}` : unitTypeLabel || '—',
      unitAccessibleLabel: unitName || 'Unit',
      unitInfo: propertyUtils.formatUnitInfo(unit.bedroomCount, unit.bathroomCount, unit.squareFeet),
      overflowLabel: unitName,
      unitNumber: unit.unitNumber,
      rent: unit.rent,
      isTotalMonthlyPrice: unit.isTotalMonthlyPrice,
      isMultifamily: unit.isMultifamily,
    };
  });

  const handleLoadMore = () => {
    const endKey = unitsData?.pager?.endKey;
    if (endKey) {
      fetchNextPageForGroup(groupId, endKey);
    }
  };

  return (
    <UnitsTableView
      variant={UNIT_TABLE_VARIANT.CARD}
      rows={rows}
      isLoading={(isFetchingByGroupId[groupId] ?? false) && units.length === 0}
      isFetching={isFetchingByGroupId[groupId] ?? false}
      hasNextPage={hasNextPageByGroupId[groupId] ?? false}
      isFetchingNextPage={isFetchingNextPageByGroupId[groupId] ?? false}
      onLoadMore={handleLoadMore}
      onArchive={onArchiveClick}
      onEdit={onEditClick}
      addRentGroupId={groupId}
      onTenantAdded={onTenantAdded}
    />
  );
};

export default UnitsTable;
